import clsx from "clsx";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faChevronDown,
  faChevronUp,
  faSparkles,
  faTriangleExclamation,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { Button } from "@/components/ui/button/Button";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { useClientInsight } from "@/services/api/agentic-v2/queries/useClientInsight";

export interface ClientInsightCardProps {
  faClientId: string;
  enabled?: boolean;
}

/**
 * AI healthcheck for one household.
 *
 * Loads the latest stored insight on mount. The user can regenerate via a
 * refresh button (synchronous LLM call — typically 5–15s).
 */
export function ClientInsightCard({
  faClientId,
  enabled = true,
}: ClientInsightCardProps) {
  const { insight, loading, refreshing, error, refreshInsight } = useClientInsight({
    faClientId,
    enabled,
  });
  const [expanded, setExpanded] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const handleRefresh = async () => {
    setRefreshError(null);
    try {
      await refreshInsight();
      setExpanded(true);
    } catch (err) {
      setRefreshError(
        err instanceof Error ? err.message : "Failed to refresh insight",
      );
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
        Loading AI insight…
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 px-4 py-4 flex dark:border-white/10 dark:bg-white/[0.02] items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <FontAwesomeIcon icon={faSparkles} className="text-indigo-500 dark:text-indigo-300" />
          No AI insight generated yet.
        </div>
        <Button
          outline
          onClick={() => void handleRefresh()}
          disabled={refreshing}
          data-testid="client-insight-generate"
          className="text-xs"
        >
          {refreshing ? "Generating…" : "Generate insight"}
        </Button>
      </div>
    );
  }

  const hc = insight.healthcheck;
  const tone = statusTone(hc.status);
  const hasDetailProse = Boolean(hc.detail?.trim());
  const hasPanes =
    hc.next_steps.length > 0 ||
    hc.risk_factors.length > 0 ||
    hc.key_insights.length > 0;
  const taskAnalysis = hc.task_analysis ?? [];
  const actionableTaskCount = taskAnalysis.filter((t) => t.actionable).length;
  const displayError = refreshError ?? error?.message ?? null;

  return (
    <div className="space-y-3" data-testid="client-insight-section">
    <div
      className={clsx(
        "rounded-xl border px-4 py-3.5",
        tone.border,
        tone.bg,
      )}
      data-testid="client-insight-card"
    >
      <div className="flex items-start gap-3">
        <span
          className={clsx(
            "mt-1 h-2.5 w-2.5 rounded-full shrink-0",
            tone.dot,
          )}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={clsx("text-[11px] font-medium uppercase tracking-wide", tone.label)}>
              {hc.status}
            </span>
            {hc.actionable && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">
                <FontAwesomeIcon icon={faTriangleExclamation} className="text-[10px]" />
                Action needed
                {hc.action_owner && (
                  <span className="text-slate-500 dark:text-slate-400 font-normal">
                    · {hc.action_owner}
                  </span>
                )}
              </span>
            )}
            {hc.lead_lost_reason && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-700 dark:text-rose-300">
                Lead lost · {hc.lead_lost_reason}
              </span>
            )}
            {taskAnalysis.length > 0 && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {actionableTaskCount}/{taskAnalysis.length} tasks actionable
              </span>
            )}
            <span className="text-[11px] text-slate-500 dark:text-slate-400 ml-auto whitespace-nowrap">
              {formatRelativeTime(insight.created_at)}
            </span>
          </div>
          {hc.health_reason && (
            <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">{hc.health_reason}</p>
          )}
          <p className="mt-1.5 text-sm text-slate-700 dark:text-slate-100 leading-relaxed">
            {hc.summary}
          </p>
          {displayError && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-300">{displayError}</p>
          )}
          {hasDetailProse && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
            >
              <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-[9px]" />
              {expanded ? "Hide details" : "Show details"}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Regenerate insight"
          className="text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="client-insight-refresh"
        >
          <FontAwesomeIcon
            icon={faArrowsRotate}
            className={clsx("text-xs", refreshing && "animate-spin")}
          />
        </button>
      </div>

      {expanded && hasDetailProse && (
        <div className="mt-3 pt-3 border-t border-slate-200 dark:border-white/5">
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-line">
            {hc.detail}
          </p>
        </div>
      )}
    </div>

    {hasPanes && (
      <div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3"
        data-testid="client-insight-panes"
      >
        {hc.next_steps.length > 0 && (
          <InsightPane title="Next steps" items={hc.next_steps} accent="emerald" />
        )}
        {hc.risk_factors.length > 0 && (
          <InsightPane title="Risks" items={hc.risk_factors} accent="amber" />
        )}
        {hc.key_insights.length > 0 && (
          <InsightPane title="Insights" items={hc.key_insights} accent="slate" />
        )}
      </div>
    )}
    </div>
  );
}

function InsightPane({
  title,
  items,
  accent,
}: {
  title: string;
  items: string[];
  accent: "emerald" | "amber" | "slate";
}) {
  const accentBorder =
    accent === "emerald"
      ? "border-emerald-500/30 dark:border-emerald-500/20"
      : accent === "amber"
      ? "border-amber-500/30 dark:border-amber-500/20"
      : "border-slate-200 dark:border-white/10";

  return (
    <section
      className={clsx(
        "rounded-xl border bg-white dark:bg-white/5 dark:ring dark:ring-white/5 dark:ring-inset",
        accentBorder,
      )}
      data-testid={`insight-pane-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <h3 className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-white/5">
        {title}
      </h3>
      <ul className="px-4 py-3 space-y-2">
        {items.map((item, idx) => (
          <InsightPaneItem key={idx} item={item} accent={accent} />
        ))}
      </ul>
    </section>
  );
}

function InsightPaneItem({
  item,
  accent,
}: {
  item: string;
  accent: "emerald" | "amber" | "slate";
}) {
  const dotClass =
    accent === "emerald"
      ? "bg-emerald-400"
      : accent === "amber"
      ? "bg-amber-400"
      : "bg-slate-500";
  return (
    <li className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
      <span className={clsx("mt-2 h-1 w-1 rounded-full shrink-0", dotClass)} aria-hidden />
      <span className="leading-snug">{item}</span>
    </li>
  );
}

function statusTone(status: string): {
  border: string;
  bg: string;
  dot: string;
  label: string;
} {
  switch (status) {
    case "critical":
      return {
        border: "border-red-500/30",
        bg: "bg-red-500/5",
        dot: "bg-red-400",
        label: "text-red-700 dark:text-red-300",
      };
    case "warning":
      return {
        border: "border-amber-500/30",
        bg: "bg-amber-500/5",
        dot: "bg-amber-400",
        label: "text-amber-700 dark:text-amber-300",
      };
    default:
      return {
        border: "border-emerald-500/20",
        bg: "bg-emerald-500/5",
        dot: "bg-emerald-400",
        label: "text-emerald-700 dark:text-emerald-300",
      };
  }
}
